if (!customElements.get('product-gallery')) {
  customElements.define(
    'product-gallery',
    class ProductGallery extends HTMLElement {
      constructor() {
        super();
        this.slider = null;
        this.statusElement = null;
        this.sliderInstance = null;
        this.statusFrame = null;
        this.videoObservers = [];
        this.isInitialized = false;
      }

      connectedCallback() {
        if (this.isInitialized) return;
        this.slider = this.querySelector('[data-product-slider]');
        this.statusElement = this.querySelector('[data-gallery-status]');
        this.videoObservers = [];
        if (!this.slider) return;
        this.isInitialized = true;
        this.viewportQuery = window.matchMedia('(min-width: 992px)');
        this.onViewportChange = () => {
          const focused = this.lightbox?.open ? this.lightboxTrigger : document.activeElement;
          const slide = this.contains(focused) ? focused.closest('[data-gallery-trigger]') : null;
          this.destroyMedia();
          this.initializeMedia();
          if (slide) this.focusMedia(slide);
        };
        this.viewportQuery.addEventListener('change', this.onViewportChange);
        this.initializeMedia();
      }

      initializeMedia() {
        this.mediaController = new AbortController();
        this.mediaRestorers = [];
        this.initSlider();
        this.handleVideoPlay();
        this.initZoom();
        this.initLightbox();
      }

      rememberAttributes(element, names) {
        const values = names.map(name => [name, element.getAttribute(name)]);
        this.mediaRestorers.push(() => values.forEach(([name, value]) => {
          if (value === null) element.removeAttribute(name);
          else element.setAttribute(name, value);
        }));
      }

      focusMedia(slide) {
        if (!slide?.isConnected || !this.isConnected) return;
        const mobile = !this.viewportQuery.matches;
        if (mobile) this.sliderInstance?.slideTo(Number(slide.dataset.mediaIndex) || 0, 0);
        const target = mobile ? slide : slide.querySelector('.media-wraper--zoomable') || slide;
        if (!target.hasAttribute('tabindex')) {
          target.setAttribute('tabindex', '-1');
          target.addEventListener('blur', () => target.removeAttribute('tabindex'), {once: true});
        }
        target.focus({preventScroll: true});
      }

      initZoom() {
        if (window.innerWidth < 992) return;
        const signal = this.mediaController.signal;
        const zoomWrapers = this.querySelectorAll('.media-wraper--zoomable');
        zoomWrapers.forEach((wraper) => {
          const image = wraper.querySelector('img');
          if (!image) return;

          this.rememberAttributes(wraper, ['tabindex', 'role', 'aria-label', 'aria-pressed']);
          wraper.setAttribute('tabindex', '0');
          wraper.setAttribute('role', 'button');
          wraper.setAttribute('aria-label', `${this.dataset.zoomLabel} ${image.alt || this.slider.querySelector('[aria-label]')?.getAttribute('aria-label') || ''}`.trim());
          wraper.setAttribute('aria-pressed', 'false');

          const zoomSrc = image.getAttribute('data_zoom_src');
          let zoomSrcRequested = false;
          let posX = 50;
          let posY = 50;

          const loadZoomSrc = () => {
            if (!zoomSrc || zoomSrcRequested) return;
            zoomSrcRequested = true;
            const highResImage = new Image();
            highResImage.onload = () => {
              if (signal.aborted || !image.isConnected) return;
              image.removeAttribute('srcset');
              image.removeAttribute('sizes');
              image.src = zoomSrc;
            };
            highResImage.src = zoomSrc;
          };

          const applyZoomPosition = () => {
            image.style.transformOrigin = `${posX}% ${posY}%`;
          };

          const updateZoomPosition = (event) => {
            const rect = wraper.getBoundingClientRect();
            posX = ((event.clientX - rect.left) / rect.width) * 100;
            posY = ((event.clientY - rect.top) / rect.height) * 100;
            applyZoomPosition();
          };

          const zoomIn = () => {
            if (wraper.classList.contains('is-zoomed')) return;
            loadZoomSrc();
            applyZoomPosition();
            wraper.classList.add('is-zoomed');
            wraper.setAttribute('aria-pressed', 'true');
            image.style.transform = 'scale(2)';
          };

          const zoomOut = () => {
            wraper.classList.remove('is-zoomed');
            wraper.setAttribute('aria-pressed', 'false');
            image.style.transform = 'scale(1)';
          };
          const originalTransform = image.style.transform;
          const originalOrigin = image.style.transformOrigin;
          this.mediaRestorers.push(() => {
            wraper.classList.remove('is-zoomed');
            image.style.transform = originalTransform;
            image.style.transformOrigin = originalOrigin;
          });

          wraper.addEventListener('click', (event) => {
            if (wraper.classList.contains('is-zoomed')) {
              zoomOut();
              return;
            }
            updateZoomPosition(event);
            zoomIn();
          }, {signal});

          wraper.addEventListener('mousemove', (event) => {
            if (!wraper.classList.contains('is-zoomed')) return;
            updateZoomPosition(event);
          }, {signal});

          wraper.addEventListener('mouseleave', () => {
            zoomOut();
          }, {signal});

          wraper.addEventListener('keydown', (event) => {
            const isZoomed = wraper.classList.contains('is-zoomed');
            const panStep = 10;

            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              if (isZoomed) {
                zoomOut();
              } else {
                posX = 50;
                posY = 50;
                zoomIn();
              }
              return;
            }

            if (event.key === 'Escape' && isZoomed) {
              event.preventDefault();
              zoomOut();
              return;
            }

            if (!isZoomed) return;

            if (event.key === 'ArrowUp') {
              event.preventDefault();
              posY = Math.max(0, posY - panStep);
              applyZoomPosition();
            } else if (event.key === 'ArrowDown') {
              event.preventDefault();
              posY = Math.min(100, posY + panStep);
              applyZoomPosition();
            } else if (event.key === 'ArrowLeft') {
              event.preventDefault();
              posX = Math.max(0, posX - panStep);
              applyZoomPosition();
            } else if (event.key === 'ArrowRight') {
              event.preventDefault();
              posX = Math.min(100, posX + panStep);
              applyZoomPosition();
            }
          }, {signal});
        });
      }

      initLightbox() {
        if (window.innerWidth >= 992) return;
        const lightbox = this.querySelector('[data-product-gallery-lightbox]');
        const lightboxSliderEl = this.querySelector('[data-lightbox-slider]');
        if (!lightbox || !lightboxSliderEl) return;
        this.lightbox = lightbox;
        const signal = this.mediaController.signal;

        const closeButton = lightbox.querySelector('[data-lightbox-close]');
        const pagination = lightbox.querySelector('.product-gallery__lightbox-pagination');
        const nextButton = lightbox.querySelector('.lightbox-next-slide-button');
        const prevButton = lightbox.querySelector('.lightbox-prev-slide-button');

        const openLightbox = (index, trigger) => {
          if (signal.aborted || !this.isConnected || lightbox.open) return;
          this.lightboxTrigger = trigger;
          lightbox.showModal();
          closeButton.focus();
          if (!this.lightboxSlider) {
            this.lightboxSlider = new Swiper(lightboxSliderEl, {
              slidesPerView: 1,
              initialSlide: index,
              grabCursor: true,
              zoom: {
                maxRatio: 3,
                minRatio: 1,
              },
              navigation: {
                nextEl: nextButton,
                prevEl: prevButton,
              },
              a11y: {
                enabled: true,
                prevSlideMessage: prevButton.getAttribute('aria-label'),
                nextSlideMessage: nextButton.getAttribute('aria-label'),
                slideLabelMessage: null,
              },
              on: {
                transitionStart: () => lightbox.querySelectorAll('video').forEach(video => video.pause()),
              },
              pagination: {
                el: pagination,
                type: 'progressbar',
              },
            });
          } else {
            this.lightboxSlider.slideTo(index, 0);
          }
        };

        this.querySelectorAll('[data-gallery-trigger]').forEach((trigger) => {
          this.rememberAttributes(trigger, ['tabindex', 'role', 'aria-haspopup', 'aria-controls']);
          trigger.setAttribute('tabindex', Number(trigger.dataset.mediaIndex) === (this.sliderInstance?.activeIndex || 0) ? '0' : '-1');
          trigger.setAttribute('role', 'button');
          trigger.setAttribute('aria-haspopup', 'dialog');
          trigger.setAttribute('aria-controls', lightbox.id);

          const activate = () => {
            const index = parseInt(trigger.dataset.mediaIndex, 10) || 0;
            openLightbox(index, trigger);
          };

          trigger.addEventListener('click', activate, {signal});
          trigger.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            activate();
          }, {signal});
        });

        closeButton.addEventListener('click', () => lightbox.close(), {signal});

        lightbox.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          event.preventDefault();
          event.stopPropagation();
          lightbox.close();
        }, {signal});

        lightbox.addEventListener('close', () => {
          if (lightbox.open || signal.aborted) return;
          lightbox.querySelectorAll('video').forEach(video => video.pause());
          this.focusMedia(this.lightboxTrigger);
          this.lightboxTrigger = null;
        }, {signal});
      }

      disconnectedCallback() {
        this.viewportQuery?.removeEventListener('change', this.onViewportChange);
        this.destroyMedia();
        this.isInitialized = false;
      }

      destroyMedia() {
        this.mediaController?.abort();
        if (this.lightbox?.open) this.lightbox.close();
        this.querySelectorAll('video').forEach(video => video.pause());
        if (this.lightboxSlider && !this.lightboxSlider.destroyed) this.lightboxSlider.destroy(true, true);
        this.lightboxSlider = null;
        this.lightboxTrigger = null;
        this.lightbox = null;
        if (this.statusFrame) cancelAnimationFrame(this.statusFrame);
        if (this.sliderInstance && !this.sliderInstance.destroyed) this.sliderInstance.destroy(true, true);
        this.videoObservers.forEach((observer) => observer.disconnect());
        this.statusFrame = null;
        this.sliderInstance = null;
        this.videoObservers = [];
        this.lastAnnouncedIndex = null;
        this.mediaRestorers?.forEach(restore => restore());
        this.mediaRestorers = [];
      }

      initSlider() {
        if (window.innerWidth >= 992) return;
        const gallery = this;
        const scrollbar = this.slider.querySelector('.product-gallery--scrollbar');
        const nextButton = this.slider.querySelector('.next-slide-button');
        const prevButton = this.slider.querySelector('.prev-slide-button');
        const slides = [...this.slider.querySelectorAll('.swiper-slide')];
        const videos = this.slider.querySelectorAll('video');
        const options = {
          slidePerView: 1,
          slidePerMove: 1,
          grabCursor: true,
          mousewheel: {
            forceToAxis: true,
          },
          navigation: {
            nextEl: nextButton,
            prevEl: prevButton,
          },
          a11y: {
            enabled: true,
            prevSlideMessage: prevButton?.getAttribute('aria-label') || '',
            nextSlideMessage: nextButton?.getAttribute('aria-label') || '',
            firstSlideMessage: slides[0]?.dataset.slideStatus || '',
            lastSlideMessage: slides[slides.length - 1]?.dataset.slideStatus || '',
            slideLabelMessage: null,
          },
          scrollbar: {
            el: scrollbar,
            draggable: true,
            dragSize: 'auto',
          },
          on: {
            slideChange: function () {
              gallery.slider.querySelectorAll('[data-gallery-trigger][aria-haspopup="dialog"]').forEach(trigger => {
                trigger.tabIndex = Number(trigger.dataset.mediaIndex) === this.activeIndex ? 0 : -1;
              });
            },
            transitionStart: function () {
              if (videos.length === 0) return;
              videos.forEach((video) => {
                video.pause();
              });
            },
            transitionEnd: function () {
              const activeSlide = this.slides[this.activeIndex];
              if (!activeSlide) return;
              gallery.updateStatus(typeof this.realIndex === 'number' ? this.realIndex : this.activeIndex, activeSlide);
              const activeSlideVideo = activeSlide.querySelector('video');
              if (!activeSlideVideo) return;
              activeSlideVideo.play();
            },
          },
        };
        this.sliderInstance = new Swiper(this.slider, options);
        this.lastAnnouncedIndex = typeof this.sliderInstance.realIndex === 'number' ? this.sliderInstance.realIndex : this.sliderInstance.activeIndex;
      }

      updateStatus(index, slide) {
        if (!this.statusElement || index === this.lastAnnouncedIndex) return;
        this.lastAnnouncedIndex = index;
        if (this.statusFrame) cancelAnimationFrame(this.statusFrame);
        this.statusElement.textContent = '';
        this.statusFrame = requestAnimationFrame(() => {
          if (this.statusElement?.isConnected) this.statusElement.textContent = slide.dataset.slideStatus || '';
          this.statusFrame = null;
        });
      }

      handleVideoPlay() {
        if (window.innerWidth < 992) return;
        const videos = this.slider.querySelectorAll('video');
        if (videos.length === 0) return;
        videos.forEach((video) => {
          this.observVideo(video);
        });
      }

      observVideo(video) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                video.play();
              } else {
                video.pause();
              }
            });
          },
          {
            threshold: 0.25,
          }
        );
        observer.observe(video);
        this.videoObservers.push(observer);
      }
    }
  );
}
